import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getOptimizationTaskStatus, getProjectByIDRequest } from 'services/apiRequests'
import {
  resetOptimizationSliceState,
  selectCreateTask,
  selectTaskId,
  selectTaskStatus,
  setTaskCalculationProgress,
  setTaskStatus,
  setErrorMessage,
  setCreateTask,
  setGetTaskResults,
  setTaskId,
} from 'store/optimizationSlice'
import { selectProjectID, selectTrunkPipelineID, setProjectDetail } from 'store/projectSlice'
import {
  resetBlockingWindow,
  selectBlockingWindow,
  selectTaskIsCanceling,
  setBlockingWindow,
  setTaskIsCanceling,
} from 'store/commonSlice'
import { definitions } from 'generated/apiTypes'
import { IBlockingWindow, ICalcProgressParams, IErrorParams } from 'store/types'

const useGetOptimizationTaskStatus = () => {
  const dispatch = useDispatch()
  const taskStatus = useSelector(selectTaskStatus)
  const taskId = useSelector(selectTaskId)
  const createTask = useSelector(selectCreateTask)
  const projectId = useSelector(selectProjectID)
  const trunkPipelineId = useSelector(selectTrunkPipelineID)
  const blockingWindow = useSelector(selectBlockingWindow) as IBlockingWindow
  const blockingWindowParams = blockingWindow?.params && (blockingWindow.params as ICalcProgressParams)
  const taskIsCanceling = useSelector(selectTaskIsCanceling)
  const timerId = React.useRef<ReturnType<typeof setTimeout>>()

  const [taskAuthor, setTaskAuthor] = React.useState<definitions['TaskStatus']['author']>()
  const [delay, setDelay] = React.useState<definitions['TaskStatus']['delay__s']>()

  const tryToGetTaskStatus = async (): Promise<definitions['TaskStatus'] | void> => {
    if (trunkPipelineId && projectId)
      return await getOptimizationTaskStatus(projectId, trunkPipelineId)
        .then((response) => {
          if (response.status === 204) dispatch(setGetTaskResults(true))
          setTaskAuthor(response.data.author)
          setDelay(response.data.delay__s)
          if (!taskId) dispatch(setTaskId(response.data.id))
          dispatch(setTaskCalculationProgress(response.data.progress))
          dispatch(setTaskStatus(response.data.status))
          if (response.data.error_message) dispatch(setErrorMessage(response.data.error_message))
          return response.data
        })
        .catch(() => {
          if (timerId.current) clearInterval(timerId.current)
          dispatch(
            setBlockingWindow({
              params: { title: 'Непредвиденная ошибка', message: 'Непредвиденная ошибка' } as IErrorParams,
              type: 'ERROR',
            }),
          )
        })
  }

  React.useEffect(() => {
    if (taskStatus) {
      if (['Рассчитан', 'Ошибка', 'Рассчитано частично', 'Отменен', 'Неактуальный'].includes(taskStatus)) {
        dispatch(resetBlockingWindow())
        dispatch(setCreateTask(false))
        dispatch(setGetTaskResults(true))
        dispatch(setTaskIsCanceling(false))
        if (timerId.current) {
          clearInterval(timerId.current)
          timerId.current = undefined
          if (projectId)
            getProjectByIDRequest(projectId).then((res: any) => {
              dispatch(setProjectDetail(res.data))
            })
        }
      } else if (!timerId.current) {
        tryToGetTaskStatus().then((returnValue) => {
          if (
            (returnValue as definitions['TaskStatus'])?.status &&
            !['Рассчитан', 'Ошибка', 'Рассчитано частично', 'Отменен', 'Неактуальный'].includes(
              (returnValue as definitions['TaskStatus']).status,
            )
          ) {
            clearInterval(timerId.current)
            timerId.current = undefined
            dispatch(
              setBlockingWindow({
                type: 'CALC_PROGRESS',
                params: {
                  taskType: 'OPTIMIZATION_TASK',
                  taskUserID: taskAuthor?.id?.toString(),
                  taskID: taskId,
                } as ICalcProgressParams,
              }),
            )
            timerId.current = setInterval(async () => {
              tryToGetTaskStatus().then()
            }, ((returnValue as definitions['TaskStatus'])?.delay__s || 3) * 1000)
          } else {
            if (projectId)
              getProjectByIDRequest(projectId).then((res: any) => {
                dispatch(setProjectDetail(res.data))
              })
          }
        })
      } else if (timerId.current) {
        clearInterval(timerId.current)
        timerId.current = setInterval(async () => {
          tryToGetTaskStatus().then()
        }, (delay || 3) * 1000)
      }
    }
  }, [taskStatus, delay])

  React.useEffect(() => {
    if (trunkPipelineId) {
      tryToGetTaskStatus().then()
    }
    return () => {
      if (timerId.current) {
        clearInterval(timerId.current)
        dispatch(resetOptimizationSliceState())
      }
    }
  }, [trunkPipelineId])

  React.useEffect(() => {
    if (taskIsCanceling && trunkPipelineId && createTask) {
      tryToGetTaskStatus().then()
      if (blockingWindow?.type) dispatch(resetBlockingWindow())
    }
  }, [taskIsCanceling])

  React.useEffect(() => {
    if (taskAuthor?.id && blockingWindow?.type === 'CALC_PROGRESS') {
      dispatch(
        setBlockingWindow({
          params: { ...blockingWindowParams, taskUserID: taskAuthor?.id?.toString() } as ICalcProgressParams,
        }),
      )
    }
  }, [taskAuthor])

  React.useEffect(() => {
    if (!blockingWindow.type && taskStatus && !taskIsCanceling) {
      if (['Запущен', 'Ожидает очереди'].includes(taskStatus)) {
        dispatch(
          setBlockingWindow({
            type: 'CALC_PROGRESS',
            params: {
              taskType: 'OPTIMIZATION_TASK',
              taskUserID: taskAuthor?.id?.toString(),
              taskID: taskId,
            } as ICalcProgressParams,
          }),
        )
      }
    }
  }, [blockingWindow.type])

  return
}

export default useGetOptimizationTaskStatus
